import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useCustomerDisplayName } from '@/sections/customerManagement/hooks/useCustomerDisplayName';

export type WorkstationMode =
  | 'query'
  | 'edit'
  | 'view'
  | 'split-edit'
  | 'split-view'
  | 'split-writer'
  | null;

export type GraphLayoutType = 'dagre' | 'cose-bilkent';
/**
 * Enriched username object with display metadata and selection state
 * Combines email identity with human-friendly display name and selection tracking
 */
export type UsernameObject = {
  /** Email address (unique identifier) */
  email: string;
  /** Human-friendly display name from customer settings */
  displayName: string;
  /** Selection state for query execution */
  isSelected: boolean;
};

/**
 * Context value type for UI state
 *
 * Separated from data context to prevent unnecessary re-renders.
 * Components that only need UI state (sidebar, mode) won't re-render on graph mutations.
 */
type QueryUIContextValue = {
  mode: WorkstationMode | null;
  previousMode: WorkstationMode | null;
  isMaximized: boolean;
  sidebarCollapsed: boolean;
  isGlobal: boolean;
  usernames: UsernameObject[];

  graphLayout: GraphLayoutType;

  setMode: (mode: WorkstationMode) => void;
  restorePreviousMode: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleMaximized: () => void;
  setIsGlobal: (isGlobal: boolean) => void;
  setUsernames: (usernames: UsernameObject[]) => void;
  setGraphLayout: (layout: GraphLayoutType) => void;
};

const QueryUIContext = createContext<QueryUIContextValue | null>(null);

/**
 * Provider component for query UI context
 *
 * Manages workstation mode, sidebar state, and maximize state.
 * Extracted from QueryBuilderContext for performance optimization.
 *
 * selectedUsernames Pattern (React 19 derived state):
 * - Computed default: All users when isGlobal=true, empty when false
 * - Manual overrides: Users can customize selection via UserSelectorDropdown
 * - Reset behavior: Override cleared when isGlobal changes, default takes effect
 */
export function QueryUIProvider({
  children,
  initialIsGlobal = false,
}: {
  children: ReactNode;
  initialIsGlobal?: boolean;
}) {
  const [mode, setModeInternal] = useState<WorkstationMode | null>(null);
  const [previousMode, setPreviousMode] = useState<WorkstationMode | null>(
    null
  );
  const [isMaximized, setIsMaximized] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarCollapsedBeforeMaximize, setSidebarCollapsedBeforeMaximize] =
    useState(false);
  const [isGlobal, setIsGlobal] = useState(initialIsGlobal);
  const [usernames, setUsernames] = useState<UsernameObject[]>([]);
  const [graphLayout, setGraphLayout] = useState<GraphLayoutType>('dagre');

  // Fetch customer display names for global mode initialization
  const { dataMap: customerDisplayNames, isLoading } = useCustomerDisplayName({
    enabled: isGlobal, // Only fetch when in global mode
  });

  // Synchronize usernames with customerDisplayNames when data loads or isGlobal changes
  useEffect(() => {
    if (!isLoading) {
      if (isGlobal && customerDisplayNames.size > 0) {
        // Transform Map entries into UsernameObject array with all selected by default
        const usernameObjects: UsernameObject[] = Array.from(
          customerDisplayNames.entries()
        ).map(([email, displayName]) => ({
          email,
          displayName,
          isSelected: true, // Default: all users selected
        }));
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setUsernames(usernameObjects);
      } else if (!isGlobal) {
        setUsernames([]);
      }
    }
    // Use customerDisplayNames.size instead of the Map itself to avoid reference changes
  }, [isGlobal, isLoading, customerDisplayNames.size]);

  const setMode = (newMode: WorkstationMode) => {
    setPreviousMode(mode);
    setModeInternal(newMode);
  };

  const restorePreviousMode = () => {
    setModeInternal(previousMode);
    setPreviousMode(null);
  };

  const toggleMaximized = () => {
    if (!isMaximized) {
      // Entering maximized mode
      setSidebarCollapsedBeforeMaximize(sidebarCollapsed);
      if (!sidebarCollapsed) {
        setSidebarCollapsed(true); // Auto-collapse sidebar
      }
    } else {
      // Exiting maximized mode
      setSidebarCollapsed(sidebarCollapsedBeforeMaximize); // Restore previous state
    }
    setIsMaximized(!isMaximized);
  };

  const value: QueryUIContextValue = {
    mode,
    previousMode,
    isMaximized,
    sidebarCollapsed,
    isGlobal,
    usernames,
    graphLayout,
    setMode,
    restorePreviousMode,
    setSidebarCollapsed,
    toggleMaximized,
    setIsGlobal,
    setUsernames,
    setGraphLayout,
  };

  return (
    <QueryUIContext.Provider value={value}>{children}</QueryUIContext.Provider>
  );
}

/**
 * Hook to access query UI context
 *
 * @throws Error if used outside QueryUIProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useQueryUI() {
  const context = useContext(QueryUIContext);
  if (!context) {
    throw new Error('useQueryUI must be used within QueryUIProvider');
  }
  return context;
}
